import { Button, ButtonVariant } from "components/Form/Button";
import { Layout } from "components/Layout";
import {
  memo,
  useCallback,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { ReactComponent as Ai } from "assets/ai.svg";
import { apiSendVerificationCode, apiVerifyCode, ApiError } from "lib/api";
import { toast } from "react-toastify";
import { SignupScreens } from "types";
import { Modal } from "components/Modal";
import { ReactComponent as Success } from "assets/success.svg";
import { Storage, StorageKey } from "lib/storage";

export const EmailVerification = memo(function EmailVerification(props: {
  email: string;
  setStep: Dispatch<SetStateAction<SignupScreens>>;
}) {
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);

  // Send initial verification code on mount
  useEffect(() => {
    const sendInitialCode = async () => {
      try {
        await apiSendVerificationCode(props.email, true);
        toast.success("Verification code sent to your email");
      } catch (err) {
        toast.error("Failed to send verification code. Please try again.");
      }
    };
    sendInitialCode();
  }, [props.email]);

  const handleCodeChange = useCallback(
    (index: number, value: string) => {
      if (value.length <= 1 && /^[0-9]*$/.test(value)) {
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 4) {
          const nextInput = document.getElementById(`signup-code-${index + 1}`);
          nextInput?.focus();
        }
      }
    },
    [code]
  );

  const handleCodeKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        const prevInput = document.getElementById(`signup-code-${index - 1}`);
        prevInput?.focus();
      }
    },
    [code]
  );

  const handleVerifyCode = useCallback(async () => {
    const codeString = code.join("");
    if (codeString.length !== 5) {
      toast.error("Please enter the complete 5-digit code");
      return;
    }

    setProcessing(true);
    try {
      await apiVerifyCode(props.email, codeString);
      setShowSuccessModal(true);
    } catch (err: any) {
      if (err instanceof ApiError) {
        toast.error(err.message || "Invalid or expired verification code");
      } else {
        toast.error("Verification failed. Please try again.");
      }
      setCode(["", "", "", "", ""]);
      // Focus first input
      document.getElementById("signup-code-0")?.focus();
    } finally {
      setProcessing(false);
    }
  }, [code, props.email]);

  const handleResendCode = useCallback(async () => {
    setProcessing(true);
    try {
      await apiSendVerificationCode(props.email, true);
      toast.success("New verification code sent to your email");
      setCode(["", "", "", "", ""]);
      document.getElementById("signup-code-0")?.focus();
    } catch (err: any) {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [props.email]);

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    props.setStep(SignupScreens.PasswordSetup);
  }, [props]);

  const handleConfirmBack = useCallback(() => {
    // Clear token and go back
    Storage.remove(StorageKey.TOKEN);
    setShowBackWarning(false);
    props.setStep(SignupScreens.EmailConfirmation);
  }, [props]);

  return (
    <>
      <Layout
        className="h-full text-center"
        toolbar={
          <>
            <Button
              disabled={code.join("").length !== 5 || processing}
              onClick={handleVerifyCode}
            >
              {processing ? "Verifying..." : "Verify Code"}
            </Button>
            <Button
              variant={ButtonVariant.FLAT}
              onClick={() => setShowBackWarning(true)}
              disabled={processing}
            >
              Back
            </Button>
          </>
        }
      >
        <div className="grid h-full">
          <div className="grid self-start justify-items-center">
            <Ai />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Verify your email
            </h1>

            <p className="text-858494 mt-3">
              We've sent a 5-digit code to
              <br />
              <strong>{props.email}</strong>
            </p>
          </div>

          <div className="text-center space-y-8">
            <div className="space-y-4">
              <p className="text-14 text-858494">Enter verification code</p>

              <div className="flex justify-center gap-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`signup-code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    disabled={processing}
                    className="w-14 h-14 text-center text-24 font-semibold border border-d1d5db rounded-lg focus:outline-none focus:ring-2 focus:ring-3461fd focus:border-transparent disabled:bg-f4f7ff disabled:cursor-not-allowed"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-14 text-858494">Didn't receive the code?</p>
              <Button
                variant={ButtonVariant.FLAT_BORDER}
                onClick={handleResendCode}
                disabled={processing}
                type="button"
              >
                Resend Code
              </Button>
            </div>

            <div className="space-y-3 items-center pt-4">
              <p className="text-14 font-semibold text-858494">
                IMPORTANT NOTE
              </p>
              <p className="text-14 text-858494">
                Check your spam folder if you don't see the email. The
                verification code is valid for 10 minutes.
              </p>
            </div>
          </div>
        </div>
      </Layout>

      <Modal show={showSuccessModal} onClose={handleSuccessModalClose}>
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <Success />
          </div>

          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Email Verified!</h2>
            <p className="text-858494">
              Your email has been successfully verified. Now let's set up your
              password.
            </p>
          </div>

          <Button onClick={handleSuccessModalClose} className="w-full">
            Continue
          </Button>
        </div>
      </Modal>

      <Modal show={showBackWarning} onClose={() => setShowBackWarning(false)}>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Exit setup?</h2>
            <p className="text-858494">
              You have already registered. If you exit now, you'll need to setup
              a password from the login screen.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setShowBackWarning(false)}
              className="flex-1"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleConfirmBack}
              variant={ButtonVariant.FLAT_BORDER}
              className="flex-1"
            >
              Exit
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
});
